import { Injectable } from '@angular/core';

/**
 * Cache de cobros cashless (tarjeta por el datáfono del cajón) para poder hacer
 * el reembolso referenciado más tarde. Soporta ambos cajones:
 *   - CASHLOGY: referencia por `transaction_number` (WIPAY, de
 *     `cashlessInfo.transactionNumber` del CHARGE_CASHLESS).
 *   - PROSEGUR: referencia por `order_id` (el orderId que devuelve el MDW en la
 *     venta), necesario para el CardRefund.
 *
 * Mismo patrón que la cache de ventas de paytef (paytef-direct.ts): un único
 * objeto en localStorage keyed por `orderno` del ticket, con TTL para no
 * crecer indefinidamente. El REFUND usa la referencia (una de las dos) para
 * ligar la devolución al cobro original de esa tarjeta.
 */

const CACHE_STORAGE_KEY = 'cashlogy_cashless_charges';
const CACHE_TTL_SECS = 24 * 3600;

// Reserva (claim) cross-contexto del abono de un cargo. El guard `_refunding`
// de UiTicketView es por instancia; dos vistas/pestañas del MISMO dispositivo
// comparten este localStorage, así que reservar aquí el cargo antes de abrir el
// abono evita que ambas lancen un CardRefund del mismo cargo en paralelo.
// Expira solo para no bloquear el cargo si la vista que reservó se cerró/cayó
// sin liberar. El TTL DEBE superar el peor caso completo de la operación: el
// modal de abono tiene su propio watchdog de 120s y aún puede tardar un poco
// más en cancelar/cerrar; con 120s la reserva podía caducar con el abono aún en
// vuelo y dejar que otra vista reclamara el mismo cargo (doble abono). 300s deja
// margen holgado por encima de ese peor caso. NOTA: no protege entre
// DISPOSITIVOS distintos (WSR); eso requeriría un lock en el backend.
const CLAIM_STORAGE_KEY = 'cashlogy_cashless_claims';
const CLAIM_TTL_SECS = 300;
// Nombre del lock (Web Locks API) que serializa el load-check-set de la reserva
// entre pestañas del mismo origen. Con localStorage a secas ese ciclo NO es
// atómico entre pestañas (dos podrían leer sin reserva, escribir y devolver
// true ambas); el Web Lock lo convierte en una sección crítica real.
const CLAIM_LOCK_NAME = 'cashlogy_cashless_claim_lock';

export interface CachedCashlessCharge {
    // Referencia del cobro para poder abonarlo. Segun el cajon:
    //   - CASHLOGY: `transaction_number` (WIPAY, de cashlessInfo).
    //   - PROSEGUR: `order_id` (el orderId que devuelve el MDW en la venta).
    transaction_number?: string | null;  // WIPAY transactionNumber (de cashlessInfo)
    order_id?: string | null;            // Prosegur MDW orderId (para CardRefund)
    peripheral_id: string | null;    // WIPAY peripheralId si el cobro lo trajo (null si no)
    // PROSEGUR: alias del datafono en el que se COBRO (PaymentTerminal,
    // PaymentTerminal2...). Con 2+ datafonos el abono (CardRefund) debe ir al
    // MISMO datafono: sin esto el back cae a PaymentTerminal por defecto y el
    // abono podria enrutarse al datafono equivocado. Null si no se fijo (1 solo
    // datafono / instalacion antigua): el back usa su PaymentTerminal.
    device_id?: string | null;       // Prosegur deviceId del cobro (para el CardRefund)
    amount: number;               // céntimos
    created_at: number;           // epoch secs
}

@Injectable({ providedIn: 'root' })
export class cashlessRefundStore {

    private nowSecs(): number {
        return Math.floor(Date.now() / 1000);
    }

    private load(): Record<string, CachedCashlessCharge> {
        try {
            const raw = localStorage.getItem(CACHE_STORAGE_KEY);
            if (!raw) return {};
            const parsed = JSON.parse(raw);
            const map = (parsed && typeof parsed === 'object') ? parsed : {};
            const pruned = cashlessRefundStore.pruneExpired(map, this.nowSecs(), CACHE_TTL_SECS);
            if (pruned > 0){
                this.save(map);
            }
            return map;
        }
        catch(e){
            return {};
        }
    }

    private save(map: Record<string, CachedCashlessCharge>){
        try {
            localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(map));
        }
        catch(e){
            console.warn('[CASHLESS-REFUND] no se pudo persistir la cache:', e);
        }
    }

    /** Elimina entradas más viejas que ttl in-place. Devuelve cuántas quitó. */
    static pruneExpired(map: Record<string, CachedCashlessCharge>, nowSecs: number, ttlSecs: number): number {
        let removed = 0;
        for (const key of Object.keys(map)){
            const entry = map[key];
            const created = (entry && typeof entry.created_at === 'number') ? entry.created_at : 0;
            if (created <= 0 || nowSecs - created >= ttlSecs){
                delete map[key];
                removed++;
            }
        }
        return removed;
    }

    remember(reference: string, entry: CachedCashlessCharge){
        // Necesitamos AL MENOS una referencia con la que abonar: el
        // transaction_number (CASHLOGY) o el order_id (PROSEGUR).
        if (!reference || !entry || (!entry.transaction_number && !entry.order_id)){
            return;
        }
        const map = this.load();
        map[reference] = entry;
        cashlessRefundStore.pruneExpired(map, this.nowSecs(), CACHE_TTL_SECS);
        this.save(map);
    }

    lookup(reference: string): CachedCashlessCharge | null {
        if (!reference) return null;
        const map = this.load();
        const entry = map[reference];
        if (!entry) return null;
        const created = entry.created_at || 0;
        if (created <= 0 || this.nowSecs() - created >= CACHE_TTL_SECS){
            return null;   // expirado
        }
        return entry;
    }

    forget(reference: string){
        if (!reference) return;
        const map = this.load();
        if (reference in map){
            delete map[reference];
            this.save(map);
        }
    }

    // Referencia estable de un cargo (order_id PROSEGUR / transaction_number
    // CASHLOGY) para indexar su reserva, independiente del ticket/alias.
    private chargeRef(orderId: string | null, transactionNumber: string | null): string {
        return 'o:' + (orderId || '') + '|t:' + (transactionNumber || '');
    }

    private loadClaims(): Record<string, number> {
        try {
            const raw = localStorage.getItem(CLAIM_STORAGE_KEY);
            const parsed = raw ? JSON.parse(raw) : {};
            return (parsed && typeof parsed === 'object') ? parsed : {};
        }
        catch(e){ return {}; }
    }

    private saveClaims(claims: Record<string, number>){
        try { localStorage.setItem(CLAIM_STORAGE_KEY, JSON.stringify(claims)); }
        catch(e){ /* almacenamiento no disponible: sin reserva, seguimos */ }
    }

    // Ejecuta `fn` dentro del Web Lock exclusivo (mutex real entre pestañas del
    // mismo origen), de modo que el load-check-set de la reserva sea una sección
    // crítica atómica. Si el runtime no soporta Web Locks (WebView antiguo) se
    // cae a ejecutar `fn` directamente (best-effort). NOTA: ni el Web Lock cubre
    // otros DISPOSITIVOS (WSR); el dedup autoritativo cross-dispositivo debe ser
    // del backend (CardRefund idempotente por cargo).
    private async withClaimLock<T>(fn: () => T): Promise<T> {
        try {
            const _locks: any = (typeof navigator !== 'undefined') ? (navigator as any).locks : null;
            if (_locks && typeof _locks.request === 'function'){
                return await _locks.request(CLAIM_LOCK_NAME, { mode: 'exclusive' }, () => fn());
            }
        }
        catch(e){ /* Web Locks no disponible/falló: best-effort abajo */ }
        return fn();
    }

    private _claimChargeSync(orderId: string | null, transactionNumber: string | null, ttlSecs: number): boolean {
        const ref = this.chargeRef(orderId, transactionNumber);
        const now = this.nowSecs();
        const claims = this.loadClaims();
        // purgar expiradas
        for (const k of Object.keys(claims)){
            if (!(typeof claims[k] === 'number') || now - claims[k] >= ttlSecs){
                delete claims[k];
            }
        }
        if (claims[ref] && now - claims[ref] < ttlSecs){
            this.saveClaims(claims);   // persistir la purga
            return false;              // reserva vigente de otra vista/pestaña
        }
        claims[ref] = now;
        this.saveClaims(claims);
        return true;
    }

    /**
     * Intenta RESERVAR el abono de un cargo antes de lanzar el CardRefund. El
     * load-check-set corre dentro de un Web Lock exclusivo (mutex real entre
     * pestañas del mismo origen), así que dos pestañas NO pueden reservar el
     * mismo cargo a la vez. Devuelve true si lo reservó, false si ya había una
     * reserva vigente (otra vista/pestaña lo está abonando).
     */
    async claimCharge(orderId: string | null, transactionNumber: string | null, ttlSecs: number = CLAIM_TTL_SECS): Promise<boolean> {
        if (!orderId && !transactionNumber) return false;
        return this.withClaimLock(() => this._claimChargeSync(orderId, transactionNumber, ttlSecs));
    }

    /** Libera la reserva de un cargo (tras abonarlo, fallar o cancelar). */
    async releaseCharge(orderId: string | null, transactionNumber: string | null): Promise<void> {
        if (!orderId && !transactionNumber) return;
        await this.withClaimLock(() => {
            const ref = this.chargeRef(orderId, transactionNumber);
            const claims = this.loadClaims();
            if (ref in claims){
                delete claims[ref];
                this.saveClaims(claims);
            }
        });
    }

    /**
     * Liquida un abono contra un cargo: descuenta `refundedCents` del importe
     * restante de TODAS las entradas (bajo cualquier clave/alias) que apunten al
     * MISMO cargo — mismo `order_id` (PROSEGUR) o `transaction_number`
     * (CASHLOGY). Un cargo puede estar cacheado bajo varias claves por dos
     * motivos y AMBOS deben quedar consistentes:
     *   - Un mismo ticket: el `_uuid` de la copia de edición + el del canónico
     *     (CopyOf), más el `orderno` de un build anterior.
     *   - VARIOS tickets: un solo cobro cubre varias comandas (guest paga
     *     Selected en un cargo) y se cachea bajo el `_uuid` de cada ticket.
     * Las entradas cuyo importe restante llega a 0 (o menos) se BORRAN; las que
     * conservan saldo quedan con el nuevo importe. Así:
     *   - Ningún alias sobrevive con el importe COMPLETO -> no se puede emitir un
     *     2º CardRefund del total (evita doble reembolso).
     *   - Los demás tickets cubiertos por el mismo cargo CONSERVAN su referencia
     *     con el saldo restante -> no caen a devolución manual tras un parcial.
     * Devuelve el importe restante (céntimos) del cargo tras el abono.
     */
    settleCharge(orderId: string | null, transactionNumber: string | null, refundedCents: number): number {
        if ((!orderId && !transactionNumber) || !(refundedCents > 0)) return 0;
        const map = this.load();
        let _remaining = 0;
        let _changed = false;
        for (const key of Object.keys(map)){
            const e = map[key];
            if (!e) continue;
            if ((orderId && e.order_id === orderId) ||
                (transactionNumber && e.transaction_number === transactionNumber)){
                const _left = (e.amount || 0) - refundedCents;
                if (_left > 0){
                    e.amount = _left;
                    _remaining = _left;
                } else {
                    delete map[key];
                }
                _changed = true;
            }
        }
        if (_changed){ this.save(map); }
        return _remaining;
    }
}
